import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Clapperboard, Megaphone, Shapes, Lightbulb, ArrowLeft } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { CtaButton } from "@/components/site/Header";
import { Squiggle, Star, Spiral, Swoosh, Scribble, Bubble, Zigzag } from "@/components/site/Doodles";
import { Reveal, SectionTitle } from "@/components/site/Reveal";
import { ProcessSteps, PaymentCard } from "@/components/site/ProcessSteps";
import { WhatsAppIcon, FacebookIcon, InstagramIcon } from "@/components/site/Icons";
import { SITE } from "@/lib/site";

const WORDS = ["براندك", "إعلانك", "لوجوك", "فكرتك"];

const RotatingWord = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % WORDS.length), 2200);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="relative inline-flex h-[1.5em] min-w-[4.2ch] items-center justify-center overflow-hidden align-middle leading-[1.5em]">
      <AnimatePresence mode="wait">
        <motion.span key={WORDS[i]} initial={{ y: "100%", rotate: -8 }} animate={{ y: 0, rotate: 0 }} exit={{ y: "-100%", rotate: 8 }}
          transition={{ type: "spring", stiffness: 260, damping: 20 }} className="inline-block text-[var(--km-cyan)]">
          {WORDS[i]}
        </motion.span>
      </AnimatePresence>
    </span>
  );
};

const Hero = () => (
  <section className="km-grid-bg relative overflow-hidden" data-testid="home-hero">
    <div className="pointer-events-none absolute -left-32 top-10 h-[28rem] w-[28rem] rounded-full bg-[var(--km-purple)]/40 blur-[120px]" />
    <div className="pointer-events-none absolute -right-20 bottom-0 h-72 w-72 rounded-full bg-[var(--km-pink)]/25 blur-[110px]" />
    <Star className="km-float absolute left-[8%] top-[18%] h-12 w-12" />
    <Spiral className="km-spin-slow absolute bottom-[14%] left-[22%] hidden h-20 w-20 md:block" />
    <Scribble className="km-wiggle absolute right-[4%] top-[12%] hidden h-24 w-24 lg:block" color="#B57AF0" />
    <Bubble className="km-float absolute bottom-[10%] right-[8%] h-16 w-16 [animation-delay:1.5s]" />
    <Zigzag className="absolute left-[40%] top-[8%] hidden h-6 w-28 opacity-70 md:block" color="#9B3BF0" />

    <div className="relative mx-auto grid max-w-7xl items-center gap-10 px-4 pb-20 pt-16 sm:px-6 md:pt-24 lg:grid-cols-[1.25fr_1fr] lg:px-8">
      <div>
        <motion.p initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}
          className="font-head mb-6 inline-flex items-center gap-2 rounded-full border border-[var(--km-cyan)]/40 bg-black/40 px-4 py-1.5 text-sm font-semibold text-[var(--km-cyan)]">
          <span className="km-blink h-2 w-2 rounded-full bg-[var(--km-pink)]" /> استوديو موشن جرافيك
        </motion.p>
        <motion.h1 initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="font-display text-6xl leading-[1.05] text-white sm:text-7xl lg:text-8xl" data-testid="hero-slogan">
          خربش على
          <span className="relative mr-3 inline-block text-[var(--km-pink)]">
            السوق
            <Squiggle className="absolute -bottom-3 right-0 h-5 w-full sm:-bottom-5 sm:h-7" color="#E9D8FF" />
          </span>
        </motion.h1>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
          className="font-head mt-10 text-2xl font-bold text-white sm:text-3xl">
          خلّي <RotatingWord /> يتحرك ويتكلم
        </motion.p>
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.65 }}
          className="mt-4 max-w-xl text-base leading-relaxed text-purple-200/90 sm:text-lg">
          احنا خرابيش موشن، استوديو بيعمل إعلانات موشن جرافيك للبراندات يوصل رسالتك في ثواني
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8 }} className="mt-9 flex flex-wrap items-center gap-4">
          <CtaButton className="px-8 py-4 text-lg" testId="hero-create-ad-btn" />
          <Link to="/portfolio" data-testid="hero-portfolio-btn"
            className="font-head group inline-flex items-center gap-2 rounded-full border-2 border-[var(--km-lilac)]/50 px-7 py-3.5 font-bold text-white transition-colors duration-200 hover:border-[var(--km-cyan)] hover:text-[var(--km-cyan)]">
            شوف شغلنا <ArrowLeft className="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-1" />
          </Link>
        </motion.div>
      </div>
      <HeroArt />
    </div>
  </section>
);

const HeroArt = () => (
  <motion.div initial={{ opacity: 0, scale: 0.85, rotate: -6 }} animate={{ opacity: 1, scale: 1, rotate: 0 }} transition={{ delay: 0.3, type: "spring", stiffness: 120 }}
    className="relative mx-auto aspect-square w-full max-w-md" aria-hidden="true">
    <div className="absolute inset-6 rotate-6 rounded-[2.5rem] bg-[var(--km-pink)]" />
    <div className="absolute inset-6 -rotate-3 grid place-items-center overflow-hidden rounded-[2.5rem] border-4 border-[var(--km-purple)] bg-white">
      <svg viewBox="0 0 300 300" className="absolute inset-0 h-full w-full">
        <motion.circle cx="150" cy="150" r="128" fill="none" stroke="#E9D8FF" strokeWidth="8" strokeDasharray="18 16"
          animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 22, ease: "linear" }} style={{ originX: "150px", originY: "150px" }} />
        <motion.circle cx="262" cy="52" r="12" fill="#7D2CC6" animate={{ y: [0, 16, 0] }} transition={{ repeat: Infinity, duration: 2.2 }} />
        <motion.circle cx="40" cy="250" r="8" fill="#9B3BF0" animate={{ y: [0, -14, 0] }} transition={{ repeat: Infinity, duration: 1.8 }} />
      </svg>
      <motion.img src="/logo-purple.png" alt="لوجو خرابيش موشن" width="1099" height="592" className="relative w-[78%]" data-testid="hero-logo"
        animate={{ rotate: [-3, 3, -3], scale: [1, 1.04, 1] }} transition={{ repeat: Infinity, duration: 3.2, ease: "easeInOut" }} />
    </div>
    <div className="font-display absolute -bottom-2 -right-2 rotate-[-8deg] rounded-2xl bg-[var(--km-cyan)] px-5 py-2 text-2xl text-[#0b0114] shadow-xl">موشن!</div>
    <Swoosh className="absolute -left-6 -top-4 h-20 w-32" color="#fff" />
  </motion.div>
);

const MARQUEE = ["موشن جرافيك", "إعلانات سوشيال", "تحريك لوجو", "فيديوهات شرح", "خربش على السوق"];
const Marquee = () => (
  <div className="relative -rotate-2 overflow-hidden border-y-4 border-black bg-[var(--km-cyan)] py-4" dir="ltr" aria-hidden="true">
    <div className="km-marquee-track flex gap-10">
      {[...MARQUEE, ...MARQUEE, ...MARQUEE, ...MARQUEE].map((w, i) => (
        <span key={i} className="font-display flex items-center gap-10 whitespace-nowrap text-3xl text-[#0b0114]">
          {w} <Star className="h-7 w-7" color="#0B0114" />
        </span>
      ))}
    </div>
  </div>
);

const SERVICES = [
  { Icon: Megaphone, title: "إعلانات سوشيال ميديا", text: "فيديوهات قصيرة متحركة معمولة مخصوص للفيسبوك وإنستجرام وتيك توك.", span: "md:col-span-2", bg: "bg-[var(--km-purple)]" },
  { Icon: Shapes, title: "تحريك اللوجو", text: "نخلي لوجو براندك يدخل بحركة تتفتكر.", span: "", bg: "bg-[var(--km-card)]" },
  { Icon: Lightbulb, title: "فيديوهات شرح", text: "نبسّط خدمتك أو منتجك في فيديو موشن سهل ومفهوم.", span: "", bg: "bg-[var(--km-card)]" },
  { Icon: Clapperboard, title: "إعلانات موشن جرافيك كاملة", text: "من الفكرة والسكريبت للتصميم والتحريك، لحد ما تستلم إعلان جاهز للنشر.", span: "md:col-span-2", bg: "bg-[var(--km-pink)]" },
];

const Services = () => (
  <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6 lg:px-8" data-testid="home-services">
    <SectionTitle kicker="بنعمل إيه؟" title="بنحوّل الكلام لحركة، والحركة لمبيعات" />
    <div className="grid gap-5 md:grid-cols-3">
      {SERVICES.map(({ Icon, title, text, span, bg }, i) => (
        <Reveal key={title} delay={i * 0.08} className={span}>
          <div className={`group relative h-full overflow-hidden rounded-[2rem] border border-purple-500/25 p-8 transition-transform duration-300 hover:-rotate-1 hover:scale-[1.02] ${bg}`} data-testid={`service-card-${i}`}>
            <Icon className="mb-6 h-12 w-12 text-white transition-transform duration-300 group-hover:-rotate-12 group-hover:scale-110" />
            <h3 className="font-head text-2xl font-bold text-white">{title}</h3>
            <p className="mt-2 max-w-md leading-relaxed text-white/85">{text}</p>
            <Scribble className="absolute -bottom-6 -left-6 h-28 w-28 opacity-30 transition-transform duration-500 group-hover:rotate-45" color="#fff" />
          </div>
        </Reveal>
      ))}
    </div>
  </section>
);

const Channels = () => (
  <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="home-channels">
    <SectionTitle kicker="أول خطوة" title="كلمنا من أي حتة تريحك" />
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {[
        { href: SITE.whatsappLink, Icon: WhatsAppIcon, label: "واتساب", sub: "رد سريع", color: "#25D366", ext: true, id: "whatsapp" },
        { href: SITE.facebook, Icon: FacebookIcon, label: "فيسبوك", sub: "Kharabeeshmotion", color: "#1877F2", ext: true, id: "facebook" },
        { href: SITE.instagram, Icon: InstagramIcon, label: "إنستجرام", sub: "kharabeeshstudio", color: "#9B3BF0", ext: true, id: "instagram" },
        { href: "/create-ad", Icon: Clapperboard, label: "فورم اعمل إعلانك", sub: "ابعت كل التفاصيل مرة واحدة", color: "#E9D8FF", ext: false, id: "form" },
      ].map(({ href, Icon, label, sub, color, ext, id }, i) => {
        const inner = (
          <>
            <span className="grid h-14 w-14 place-items-center rounded-2xl text-white transition-transform duration-300 group-hover:rotate-12" style={{ background: color }}>
              <Icon className="h-7 w-7" />
            </span>
            <span>
              <span className="font-head block text-lg font-bold text-white">{label}</span>
              <span className="block text-sm text-purple-200/75" dir={ext ? "ltr" : "rtl"}>{sub}</span>
            </span>
          </>
        );
        const cls = "group flex items-center gap-4 rounded-3xl border border-purple-500/25 bg-[var(--km-card)] p-5 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-white/50";
        return (
          <Reveal key={id} delay={i * 0.06}>
            {ext ? <a href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`channel-${id}-link`}>{inner}</a>
              : <Link to={href} className={cls} data-testid={`channel-${id}-link`}>{inner}</Link>}
          </Reveal>
        );
      })}
    </div>
  </section>
);

const FinalCta = () => (
  <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
    <Reveal>
      <div className="relative overflow-hidden rounded-[2.5rem] bg-[var(--km-purple)] px-8 py-16 sm:px-14">
        <Spiral className="km-spin-slow absolute -left-8 -top-8 h-40 w-40 opacity-40" color="#fff" />
        <Star className="km-float absolute bottom-6 left-1/3 h-10 w-10" color="#9B3BF0" />
        <h2 className="font-display relative max-w-2xl text-4xl leading-tight text-white sm:text-6xl">جاهز تخربش على السوق؟</h2>
        <p className="relative mt-4 max-w-xl text-lg text-white/85">ابعتلنا تفاصيل براندك في دقيقتين، واحنا هنكلمك على واتساب نكمل الكلام.</p>
        <CtaButton className="relative mt-8 px-8 py-4 text-lg" testId="final-create-ad-btn" />
      </div>
    </Reveal>
  </section>
);

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo description="خرابيش موشن - استوديو موشن جرافيك بيعمل إعلانات متحركة للبراندات على السوشيال ميديا. خربش على السوق واعمل إعلانك دلوقتي." />
      <Hero />
      <Marquee />
      <Services />
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="home-process">
        <SectionTitle kicker="الشغل ماشي إزاي؟" title="4 خطوات وإعلانك يبقى جاهز" />
        <ProcessSteps />
        <PaymentCard />
      </section>
      <Channels />
      <FinalCta />
    </div>
  );
}
